import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Fade,
  Card,
  CardActionArea,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Shield as ShieldIcon,
  LocalParking,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { BRANDING } from '../../branding';

const ParkeyLogoBox = ({ width = 500, height = 320 }) => {
  return (
    <Box
      component="img"
      src={BRANDING.logoLogin}
      alt={BRANDING.nombre}
      sx={{
        width: `${width}px`,
        height: `${height}px`,
        objectFit: 'contain',
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
        maxWidth: '100%'
      }}
    />
  );
};

const ParqueaderoSelector = ({ clientes, onSelect, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState(null);

  const handleSelect = async (clienteId) => {
    setSelecting(clienteId);
    setLoading(true);
    setError('');
    const result = await onSelect(clienteId);
    if (!result.success) {
      setError(result.error || 'Error seleccionando parqueadero');
      setLoading(false);
      setSelecting(null);
    }
  };

  return (
    <Fade in timeout={400}>
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%', bgcolor: 'rgba(245,158,11,0.15)', mb: 2 }}>
            <LocalParking sx={{ fontSize: 36, color: '#F59E0B' }} />
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: 'white', mb: 1 }}>
            Selecciona un parqueadero
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Tu usuario tiene acceso a múltiples parqueaderos.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336', border: '1px solid rgba(244,67,54,0.3)', '& .MuiAlert-icon': { color: '#f44336' } }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {clientes.map(c => (
            <Card key={c.id} sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, transition: 'all 0.2s', '&:hover': { border: '1px solid rgba(245,158,11,0.5)', bgcolor: 'rgba(245,158,11,0.08)' } }}>
              <CardActionArea onClick={() => !loading && handleSelect(c.id)} disabled={loading}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 2 }}>
                  {c.logo_url ? (
                    <Box component="img" src={c.logo_url} sx={{ width: 40, height: 40, borderRadius: 1.5, objectFit: 'contain', bgcolor: '#fff', p: '3px' }} />
                  ) : (
                    <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <LocalParking sx={{ color: '#F59E0B', fontSize: 22 }} />
                    </Box>
                  )}
                  <Typography fontWeight={600} sx={{ color: 'white', flex: 1 }}>{c.nombre}</Typography>
                  {selecting === c.id && <CircularProgress size={18} sx={{ color: '#F59E0B' }} />}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        <Button variant="text" onClick={onCancel} disabled={loading}
          sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', '&:hover': { color: 'rgba(255,255,255,0.8)' } }}>
          Volver al inicio de sesión
        </Button>
      </Box>
    </Fade>
  );
};

const SucursalSelector = ({ sucursales, onSelect, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState(null);

  const handleSelect = async (sucursalId) => {
    setSelecting(sucursalId);
    setLoading(true);
    setError('');
    const result = await onSelect(sucursalId);
    if (!result.success) {
      setError(result.error || 'Error seleccionando sucursal');
      setLoading(false);
      setSelecting(null);
    }
  };

  return (
    <Fade in timeout={400}>
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%', bgcolor: 'rgba(245,158,11,0.15)', mb: 2 }}>
            <LocalParking sx={{ fontSize: 36, color: '#F59E0B' }} />
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: 'white', mb: 1 }}>
            Elige tu sucursal
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Tu usuario está asignado a varias sucursales. Elige con cuál trabajar.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336', border: '1px solid rgba(244,67,54,0.3)', '& .MuiAlert-icon': { color: '#f44336' } }}>
            {error}
          </Alert>
        )}

        {sucursales.length === 0 && !error && (
          <Alert severity="warning" sx={{ mb: 2, bgcolor: 'rgba(245,158,11,0.1)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }}>
            No se pudieron cargar tus sucursales. Intenta iniciar sesión de nuevo.
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {sucursales.map(s => (
            <Card key={s.id} sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, transition: 'all 0.2s', '&:hover': { border: '1px solid rgba(245,158,11,0.5)', bgcolor: 'rgba(245,158,11,0.08)' } }}>
              <CardActionArea onClick={() => !loading && handleSelect(s.id)} disabled={loading}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <LocalParking sx={{ color: '#F59E0B', fontSize: 22 }} />
                  </Box>
                  <Typography fontWeight={600} sx={{ color: 'white', flex: 1 }}>{s.nombre}</Typography>
                  {selecting === s.id && <CircularProgress size={18} sx={{ color: '#F59E0B' }} />}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        <Button variant="text" onClick={onCancel} disabled={loading}
          sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', '&:hover': { color: 'rgba(255,255,255,0.8)' } }}>
          Volver al inicio de sesión
        </Button>
      </Box>
    </Fade>
  );
};

const Login = () => {
  const {
    login, clientesDisponibles, selectCliente, cancelClienteSelection,
    sucursalesPendientes, needsSucursalSelection, selectSucursal, cancelSucursalSelection,
  } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validación básica del frontend
    if (!formData.email.trim()) {
      setError('El nombre de usuario es requerido');
      setLoading(false);
      return;
    }

    if (!formData.password) {
      setError('La contraseña es requerida');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      const result = await login(formData.email.trim(), formData.password);
      if (!result.success) {
        setError(result.error || 'Error de autenticación');
      }
    } catch (err) {
      console.error('Error en login:', err);
      setError('Error de conexión. Verifica tu conexión a internet e inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex' }}>
      <Box
        sx={{
          flex: 1,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#1a1a1a',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 20%, rgba(245, 158, 11, 0.03) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(217, 119, 6, 0.02) 0%, transparent 50%),
              radial-gradient(circle at 40% 60%, rgba(251, 191, 36, 0.02) 0%, transparent 50%)
            `,
            pointerEvents: 'none'
          }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            left: '8%',
            width: 120,
            height: 120,
            background: 'linear-gradient(135deg, #F59E0B20, #D9770620)',
            borderRadius: '30px',
            transform: 'rotate(15deg)',
            animation: 'float 8s ease-in-out infinite',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%',
              height: '60%',
              background: 'linear-gradient(135deg, #F59E0B15, #D9770615)',
              borderRadius: '20px'
            }
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: '25%',
            right: '12%',
            width: 80,
            height: 80,
            background: 'linear-gradient(135deg, #F59E0B25, #FBBF2420)',
            borderRadius: '50%',
            animation: 'float 10s ease-in-out infinite reverse',
            boxShadow: '0 8px 32px rgba(245, 158, 11, 0.1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '20%',
              left: '20%',
              width: '60%',
              height: '60%',
              background: 'linear-gradient(135deg, #ffffff40, #f8f9fa30)',
              borderRadius: '50%',
              backdropFilter: 'blur(10px)'
            }
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '15%',
            width: 100,
            height: 100,
            background: 'linear-gradient(135deg, #D9770615, #F59E0B10)',
            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
            animation: 'float 12s ease-in-out infinite',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '70%',
              height: '70%',
              background: 'linear-gradient(135deg, #ffffff30, transparent)',
              clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              backdropFilter: 'blur(5px)'
            }
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: '60%',
            right: '20%',
            width: 60,
            height: 60,
            border: '2px solid',
            borderColor: 'rgba(245, 158, 11, 0.2)',
            borderRadius: '50%',
            animation: 'float 7s ease-in-out infinite reverse',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '40%',
              height: '40%',
              background: '#F59E0B',
              borderRadius: '50%',
              opacity: 0.6
            }
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            bottom: '35%',
            right: '8%',
            width: 40,
            height: 40,
            background: 'linear-gradient(45deg, #F59E0B30, #FBBF2425)',
            borderRadius: '8px',
            transform: 'rotate(45deg)',
            animation: 'float 9s ease-in-out infinite',
            boxShadow: '0 4px 20px rgba(245, 158, 11, 0.15)'
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '5%',
            width: '1px',
            height: '200px',
            background: 'linear-gradient(to bottom, transparent, rgba(245, 158, 11, 0.3), transparent)',
            transform: 'rotate(15deg)',
            animation: 'float 15s ease-in-out infinite'
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: '25%',
            width: '150px',
            height: '1px',
            background: 'linear-gradient(to right, transparent, rgba(245, 158, 11, 0.4), transparent)',
            transform: 'rotate(-10deg)',
            animation: 'float 13s ease-in-out infinite reverse'
          }}
        />

        <Fade in={true} timeout={1000}>
          <Box sx={{ textAlign: 'center', px: 4, zIndex: 10, position: 'relative' }}>
            <Box sx={{ mb: 4 }}>
              <ParkeyLogoBox
                width={320}
                height={84}
              />
            </Box>

            <Typography 
              variant="h3" 
              fontWeight="bold" 
              sx={{ 
                mb: 3, 
                lineHeight: 1.2, 
                color: '#1a1a1a',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '3px',
                  background: 'linear-gradient(90deg, #F59E0B, #FBBF24)',
                  borderRadius: '2px'
                }
              }}
            >
              Control Vehicular Inteligente
            </Typography>

            <Typography 
              variant="h6" 
              sx={{ 
                mb: 5, 
                color: '#4a4a4a',
                maxWidth: 450, 
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400,
                letterSpacing: '0.5px'
              }}
            >
              Solución avanzada para el control inteligente de parqueaderos 
              y gestión profesional de accesos vehiculares en tiempo real.
            </Typography>

            <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center', gap: 6 }}>
              {[
                { icon: <SecurityIcon sx={{ fontSize: 28 }} />, label: 'Seguro', desc: 'Máxima protección' },
                { icon: <ShieldIcon sx={{ fontSize: 28 }} />, label: 'Confiable', desc: '99.9% uptime' },
                { icon: <AnalyticsIcon sx={{ fontSize: 28 }} />, label: 'Analítico', desc: 'Reportes en tiempo real' }
              ].map((item, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    textAlign: 'center',
                    p: 3,
                    borderRadius: 3,
                    background: 'rgba(245, 158, 11, 0.03)',
                    border: '1px solid rgba(245, 158, 11, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      background: 'rgba(245, 158, 11, 0.08)',
                      border: '1px solid rgba(245, 158, 11, 0.2)',
                      boxShadow: '0 8px 30px rgba(245, 158, 11, 0.15)'
                    }
                  }}
                >
                  <Box sx={{ 
                    mb: 2, 
                    color: '#F59E0B',
                    p: 2,
                    borderRadius: '50%',
                    background: 'rgba(245, 158, 11, 0.1)',
                    display: 'inline-flex'
                  }}>
                    {item.icon}
                  </Box>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#1a1a1a', mb: 1 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    {item.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Fade>
      </Box>

      {/* Panel Derecho - Formulario de Login / Selector de parqueadero */}
      <Box
        sx={{
          flex: 1,
          background: '#282626ff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: 4
        }}
      >
        {clientesDisponibles.length > 0 ? (
          <ParqueaderoSelector
            clientes={clientesDisponibles}
            onSelect={selectCliente}
            onCancel={cancelClienteSelection}
          />
        ) : needsSucursalSelection ? (
          <SucursalSelector
            sucursales={sucursalesPendientes}
            onSelect={selectSucursal}
            onCancel={cancelSucursalSelection}
          />
        ) : (
        <Fade in={true} timeout={1200}>
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            {/* Header del login */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                sx={{ color: 'white', mb: 2 }}
              >
                Iniciar Sesión
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}
              >
                Ingresa tus credenciales para acceder al sistema de gestión 
                de control vehicular profesional.
              </Typography>
            </Box>

            {/* Alerta de error */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  bgcolor: 'rgba(244, 67, 54, 0.1)',
                  color: '#f44336',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  '& .MuiAlert-icon': {
                    color: '#f44336'
                  }
                }}
              >
                {error}
              </Alert>
            )}

            {/* Formulario */}
            <form onSubmit={handleSubmit}>
              {/* Campo Usuario */}
              <Typography 
                variant="body2" 
                sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}
              >
                Correo electrónico o usuario
              </Typography>
              <TextField
                fullWidth
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Ingresa tu correo o nombre de usuario"
                required
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 1,
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#F59E0B',
                    },
                    '& input': {
                      color: 'white',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)'
                      }
                    }
                  }
                }}
                disabled={loading}
              />

              {/* Campo Contraseña */}
              <Typography 
                variant="body2" 
                sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}
              >
                Contraseña
              </Typography>
              <TextField
                fullWidth
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder="Ingresa tu contraseña"
                required
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 1,
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#F59E0B',
                    },
                    '& input': {
                      color: 'white',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)'
                      }
                    }
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={loading}
              />

              {/* Checkbox Recordarme */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-checked': {
                        color: '#F59E0B',
                      },
                    }}
                    disabled={loading}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Mantener sesión iniciada
                  </Typography>
                }
                sx={{ mb: 4 }}
              />

              {/* Botón de Login */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  mb: 4,
                  bgcolor: '#F59E0B',
                  borderRadius: 25,
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#D97706',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(245, 158, 11, 0.5)'
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} color="inherit" />
                    <Typography>Iniciando sesión...</Typography>
                  </Box>
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>
            </form>

            {/* Información adicional */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography 
                variant="caption" 
                sx={{ color: 'rgba(255, 255, 255, 0.5)', lineHeight: 1.4 }}
              >
                ¿Problemas para iniciar sesión? Contacta al administrador del sistema
              </Typography>
            </Box>
          </Box>
        </Fade>
        )}
      </Box>

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            33% { transform: translateY(-15px) rotate(2deg); }
            66% { transform: translateY(-8px) rotate(-1deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default Login;